import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, EMPTY, Observable, Subscription, combineLatest, throwError } from 'rxjs';
import { filter, switchMap, tap, catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { MailingListsService } from '../../../../../Common/MailingLists/services/mailing-lists.service';
import { AuditableFilterService } from '../../../../../Common/Filter/services/auditable-filter.service';
import { DateHelper } from '../../../../../Common/Forms/helpers/date.helper';
import { AuditableModel } from '../../../../../Common/Misc/models/auditable.model';
import { UsersService } from '../../../../../Core/Users/services/users.service';
import { DistributionRecipientModel } from '../../../models/distribution-recipient-model';
import { RecipientInformationModel } from '../../../models/recipient-information.model';
import { TargetingListList } from '../../../models/targeting-list-list.model';
import { TargetingSearchList } from '../../../models/targeting-search-list.model';
import { DistributionService } from '../../../../../Common/TargetingShared/services/distribution.service';
import { TargetingListService } from '../../../services/targeting-list.service';
import { TargetingSearchesService } from '../../../services/targeting-searches.service';
import { DistributionApi } from '../../../../../api/distributions/distribution.api';
import { PortfolioService } from '../../Portfolios/portfolio-service';
import { RecipientType } from '../../../enums/recipient-type.enum';
import { DistributionSourceType } from '../../../enums/distribution-source-type.enum';
import { ExcludedRecipientApiModel } from '../../../../../api/distributions/model/excluded-recipient.api.model';
import { TargetingResultModel } from '../../../models/targeting-result.model';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { sortArrayByString } from '../../../../../Common/Misc/helpers/sort/sort.helper';
import { FilterTypeModel } from '../../../../../Common/Filter/models/abstract-filter-type.model';
import { FilterTypeRadioModel } from '../../../../../Common/Filter/models/filter-type-radio.model';
import { ListEntitiesVisibilityEnum } from '../../../../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { FilterTypeCheckboxModel } from '../../../../../Common/Filter/models/filter-type-checkbox.model';
import { TargetedListTypesEnum } from '../../../../../Common/TargetingShared/enum/targeted-list-types.enum';
import { SearchModeEnum } from '../../../../../Common/TargetingShared/enum/search-mode.enum';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import {
    UserAppPreferenceSavedSearchesFilters,
    UserAppPreferenceTargetedListsFilters
} from '@Core/Users/models/user-app-preferences-filters.model';
import { UserModel } from '../../../../../Core/Users/models/user.model';
import { ToggleAction } from '../../../enums/toggle-action.enum';


export class RecipientRow implements AuditableModel {
    selected?: boolean;
    countLoading?: boolean = false;

    id?: number;
    count?: number;
    contactCount?: number;
    outletCount?: number;
    name?: string;
    createdByUserId?: number;
    createdDate?: Date;
    modifiedByUserId?: number;
    modifiedDate?: Date;
    editDisabled?: boolean = false;

    createdUser?: string;
    createdDateString?: string;
    modifiedUser?: string;
    modifiedDateString?: string;
    hidden?: boolean;

    // TargetingListList specific properties
    hasContacts?: boolean;
    hasOutlets?: boolean;

    // TargetingSearchList specific properties
    searchType?: SearchModeEnum;

    constructor(summaryModel?: TargetingListList | TargetingSearchList) {
        if (summaryModel) {
            this.id = summaryModel.id;
            this.count = summaryModel.count;
            this.name = summaryModel.name;
            this.createdByUserId = summaryModel.createdByUserId;
            this.createdDate = summaryModel.createdDate;
            this.createdDateString = DateHelper.toDateString(summaryModel.createdDate);
            this.modifiedByUserId = summaryModel.modifiedByUserId;
            this.modifiedDate = summaryModel.modifiedDate;
            this.modifiedDateString = summaryModel.modifiedDate ? DateHelper.toDateString(summaryModel.modifiedDate) : null;

            if (summaryModel instanceof TargetingListList) {
                this.hasContacts = summaryModel.hasContacts;
                this.hasOutlets = summaryModel.hasOutlets;
                this.hidden = summaryModel.hidden;
            }

            if (summaryModel instanceof TargetingSearchList) {
                this.searchType = summaryModel.type;
                this.hidden = summaryModel.hidden;
            }
        }
    }
}

@Injectable()
export class RecipientEditorService extends AuditableFilterService implements OnDestroy {

    // Actual data
    listRecipientRows: RecipientRow[] = [];
    searchRecipientRows: RecipientRow[] = [];
    // currently not being used, but might add it down the road
    // mailingListRecipientRows: RecipientRow[] = [];
    // portfolioRecipientRows: RecipientRow[] = [];
    sourceType: DistributionSourceType = DistributionSourceType.lists;

    // Reference to either list/search for the view
    public recipientRows: RecipientRow[] = this.listRecipientRows;

    private filteredRecipientRows: RecipientRow[] = this.recipientRows;
    private filteredRecipientRows$ = new BehaviorSubject<RecipientRow[]>(this.filteredRecipientRows);

    private currentRecipientInformation: RecipientInformationModel = null;
    private currentRecipientInformation$ = new BehaviorSubject<RecipientInformationModel>(null);

    private excludedRecipients: ExcludedRecipientApiModel[];
    private excludedRecipients$ = new BehaviorSubject<ExcludedRecipientApiModel[]>(null);

    private allFilters: FilterTypeModel[];
    private listUserPrefsFilters: UserAppPreferenceTargetedListsFilters;
    private searchUserPrefsFilters: UserAppPreferenceSavedSearchesFilters;

    private distributionId: number = null;

    private subscriptions: Subscription[] = [];

    readonly visibilityFilter = new FilterTypeRadioModel<ListEntitiesVisibilityEnum>(
        'visibility',
        'Monitoring.filter.visibility',
        [
            {
                label: 'Monitoring.filter.visibility.all',
                value: ListEntitiesVisibilityEnum.all
            },
            {
                label: 'Monitoring.filter.visibility.visible',
                value: ListEntitiesVisibilityEnum.visible
            },
            {
                label: 'Monitoring.filter.visibility.hidden',
                value: ListEntitiesVisibilityEnum.hidden
            }
        ]
    );

    readonly listTypeFilter = new FilterTypeCheckboxModel<TargetedListTypesEnum>(
        'listType',
        'Discover.recipientEditor.filters.list.type',
        [
            {
                label: 'Discover.leftSidebar.filter.listType.option.both',
                value: TargetedListTypesEnum.both,
                count: 0
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.contact',
                value: TargetedListTypesEnum.contact,
                count: 0
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.outlet',
                value: TargetedListTypesEnum.outlet,
                count: 0
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.empty',
                value: TargetedListTypesEnum.empty,
                count: 0
            }
        ]
    );

    readonly searchTypeFilter = new FilterTypeRadioModel<SearchModeEnum>(
        'searchType',
        'Discover.recipientEditor.filters.savedSearch.type',
        [
            {
                label: 'Discover.leftSidebar.filter.searchType.option.contact',
                value: SearchModeEnum.contact,
                count: 0
            },
            {
                label: 'Discover.leftSidebar.filter.searchType.option.outlet',
                value: SearchModeEnum.outlet,
                count: 0
            }
        ]
    );

    constructor(
        public distributionService: DistributionService,
        public mailingListsService: MailingListsService,
        public targetingListService: TargetingListService,
        public targetingSearchesService: TargetingSearchesService,
        public distributionApi: DistributionApi,
        public usersService: UsersService,
        public portfolioService: PortfolioService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public userPreferencesService: UserPreferencesService
    ) {
        super(usersService);
        this.allFilters = [
            this.nameFilter,
            this.listTypeFilter,
            this.createdByFilter,
            this.createdDateFilter,
            this.modifiedByFilter,
            this.modifiedDateFilter,
            this.visibilityFilter
        ];
        this.setFilters(this.allFilters);
        this.applyFilters();

        this.subscriptions.push(
            this.distributionService.getFocusedResource().pipe(
                filter(x => !!x),
            ).subscribe(distribution => {
                this.currentRecipientInformation = distribution.recipientInformation.clone();
                this.currentRecipientInformation$.next(this.currentRecipientInformation);

                this.setSelections();

                this.excludedRecipients = !!distribution?.excludedRecipients ? distribution.excludedRecipients.slice() : [];
                this.excludedRecipients$.next(this.excludedRecipients);

                this.distributionId = distribution.id;
            }),
            combineLatest([
                this.targetingListService.all$.pipe(filter(x => !!x)),
                this.targetingSearchesService.all$.pipe(filter(x => !!x)),
            ]).pipe(
                switchMap(([lists, searches]) => {

                    this.listRecipientRows = lists?.map(x => new RecipientRow(x));
                    this.searchRecipientRows = searches?.map(x => new RecipientRow(x));

                    const userIdSet = new Set<number>();
                    [...lists, ...searches]?.forEach(x => {
                        userIdSet.add(x.createdByUserId);
                        userIdSet.add(x.modifiedByUserId);
                    });
                    return this.usersService.getUsers(Array.from(userIdSet));
                })
            ).subscribe((users: UserModel[]) => {
                const userMap = new Map<number, UserModel>(users.map(user => [user.id, user]));

                this.updateUserLabels(this.searchRecipientRows, userMap);
                this.searchRecipientRows = this.searchRecipientRows.slice();
                sortArrayByString(this.searchRecipientRows, 'name');

                this.updateUserLabels(this.listRecipientRows, userMap);
                this.listRecipientRows = this.listRecipientRows.slice();
                sortArrayByString(this.listRecipientRows, 'name');

                this.setSelections();

                // is needed for new step layout, because it is "setting" the recipient tab, before the return getTargetingLists
                this.changeSourceType(this.sourceType, false);

                // sometimes the targetingLists get loaded after everything.. and apply filters call all the .next()
                // so things like add/edit recipients in distributions, once the api call is returned.. it does not get loaded...
                // but no one has brought it up, and it's a rare usecase after a refresh browser
                // but I'm nervous it will break other things (end of sprint).. will keep it here commented incase
                // this.applyFilters();
            }),
            // currently not being used, but might add it down the road
            // this.portfolioService.getPortfolios().pipe(filter(x => !!x)).subscribe(portfolios => {
            //     this.portfolioRecipientRows = portfolios.map(x => {
            //         const row = new RecipientRow();
            //         row.id = x.targetingListId;
            //         row.count = x.count;
            //         row.name = x.metadata.displayName;
            //         return row;
            //     });
            // }),
            // currently not being used, but might add it down the road
            // this.mailingListsService.getMailingLists().pipe(filter(lists => !!lists)).subscribe(mailingLists => {
            //     this.mailingListRecipientRows.length = 0;
            //     mailingLists
            //         .map(x => new RecipientRow(x))
            //         .forEach(x => this.mailingListRecipientRows.push(x));
            //     this.mailingListRecipientRows.sort(sortByName);
            //     this.setSelections();
            //     this.applyFilters();
            // }),
            this.userPreferencesService.getUserAppPreferences().pipe(
                tap(userAppPrefs => {
                    this.listUserPrefsFilters = userAppPrefs.targetingPrefs.targetedListsPrefs.defaultFilters;
                    this.searchUserPrefsFilters = userAppPrefs.targetingPrefs.savedSearchesPrefs.defaultFilters;
                    this.applyPreferenceFilters();
                })
            ).subscribe()

        );
    }

    private updateUserPreferenceFilters(): void {
        const filterOptions = [
            { filterId: 'name', prefProp: 'name' },
            { filterId: 'listType', prefProp: 'type' },
            { filterId: 'createdBy', prefProp: 'createdBy' },
            { filterId: 'modifiedBy', prefProp: 'modifiedBy' },
            { filterId: 'createdDate', prefProp: 'createdDate' },
            { filterId: 'modifiedDate', prefProp: 'modifiedDate' },
            { filterId: 'visibility', prefProp: 'visibility' },
            { filterId: 'searchType', prefProp: 'type' }
        ];

        let userPreferenceFilters: UserAppPreferenceSavedSearchesFilters | UserAppPreferenceTargetedListsFilters;

        if (this.sourceType === DistributionSourceType.lists) {
            userPreferenceFilters = this.listUserPrefsFilters;
        } else if (this.sourceType === DistributionSourceType.searches) {
            userPreferenceFilters = this.searchUserPrefsFilters;
        }

        if (!userPreferenceFilters) {
            return;
        }

        this.filters.forEach(_filter => {
            const option = filterOptions.find(o => o.filterId === _filter.id);
            if (!option) {
                return;
            }
            userPreferenceFilters[option.prefProp] = _filter.getCurrentValue();
        });
    }

    applyFilters() {
        // Sets the selected values from the dialog into the model
        super.applyFilters();

        const listFilterValue = this.listTypeFilter.getValue();
        const searchFilterValue = this.searchTypeFilter.getValue();
        const visibilityFilterValue = this.visibilityFilter.getValue();

        this.updateUserPreferenceFilters();

        this.applyPreferenceFilters(false);
        this.filteredRecipientRows = this.applyAuditableFilters(this.recipientRows);

        // apply list type filter
        if (this.sourceType === DistributionSourceType.lists && (listFilterValue?.length > 0)) {
            this.filteredRecipientRows = this.filteredRecipientRows.filter(row => {
                const both = listFilterValue.includes(TargetedListTypesEnum.both);
                const hasContact = listFilterValue.includes(TargetedListTypesEnum.contact);
                const hasOutlet = listFilterValue.includes(TargetedListTypesEnum.outlet);
                const empty = listFilterValue.includes(TargetedListTypesEnum.empty);

                const isGood = (both && row.hasContacts && row.hasOutlets)
                    || (hasContact && row.hasContacts && !row.hasOutlets)
                    || (hasOutlet && row.hasOutlets && !row.hasContacts)
                    || (empty && !row.hasContacts && !row.hasOutlets);

                return isGood;
            });
        }

        // apply search type filter
        if (this.sourceType === DistributionSourceType.searches) {
            this.filteredRecipientRows = this.filteredRecipientRows.filter(row => {
                if (searchFilterValue === SearchModeEnum.contact) {
                    return row.searchType === SearchModeEnum.contact;
                } else if (searchFilterValue === SearchModeEnum.outlet) {
                    return row.searchType === SearchModeEnum.outlet;
                } else {
                    return row;
                }
            });
        }

        // apply visibility filter
        this.filteredRecipientRows = this.filteredRecipientRows.filter(row => {
            switch (visibilityFilterValue) {
                case ListEntitiesVisibilityEnum.all:
                    return row;
                case ListEntitiesVisibilityEnum.visible:
                    return !row.hidden;
                case ListEntitiesVisibilityEnum.hidden:
                    return row.hidden;
                default:
                    return row;
            }
        });

        if (this.currentRecipientInformation) {
            const searchGroup = this.currentRecipientInformation.recipientGroups.find(x => x.type === RecipientType.search);
            if (searchGroup) {
                const lockedSearchIds = searchGroup.recipients.filter(x => x.isLocked).map(x => x.targetingSearchId) || [];
                this.recipientRows.forEach(row => {
                    row.editDisabled = lockedSearchIds.includes(row.id);
                });
            }
        }

        this.filteredRecipientRows$.next(this.filteredRecipientRows);
        this.filters$.next(this.filters);
    }

    setSelections() {
        if (this.currentRecipientInformation) {
            const listRecipients = this.currentRecipientInformation.getGroupRecipientsByRecipientType(RecipientType.list);
            for (const row of this.listRecipientRows) {
                row.selected = !!listRecipients.find(x => x.targetingListId === row.id);
            }

            // currently not being used, but might add it down the road
            // const portfolioRecipients = this.currentRecipientInformation.getGroupRecipientsByRecipientType(RecipientType.portfolio);
            // for (const row of this.portfolioRecipientRows) {
            //     row.selected = !!portfolioRecipients.find(x => x.portfolioId === row.id);
            // }

            const searchRecipients = this.currentRecipientInformation.getGroupRecipientsByRecipientType(RecipientType.search);
            for (const row of this.searchRecipientRows) {
                row.selected = !!searchRecipients.find(x => x.targetingSearchId === row.id);
            }

            // currently not being used, but might add it down the road
            // const mailingListRecipients = this.currentRecipientInformation.getGroupRecipientsByRecipientType(RecipientType.mailingList);
            // for (const row of this.mailingListRecipientRows) {
            //     row.selected = !!mailingListRecipients.find(x => x.mailingListId === row.id);
            // }
        }
    }

    updateRecipients() {
        this.distributionService.updateRecipients(this.currentRecipientInformation, this.excludedRecipients);
    }

    changeSourceType(sourceType: DistributionSourceType, clearFilters: boolean = true) {
        this.sourceType = sourceType;
        const nameFilter = this.filters.find(x => x.id === 'name');

        switch(sourceType) {
            case DistributionSourceType.searches:
                this.recipientRows = this.searchRecipientRows;

                const searchGroup = this.currentRecipientInformation.recipientGroups.find(x => x.type === RecipientType.search);
                if (searchGroup) {
                    const lockedSearchIds = searchGroup.recipients.filter(x => x.isLocked).map(x => x.targetingSearchId) || [];
                    this.recipientRows.forEach(row => {
                        row.editDisabled = lockedSearchIds.includes(row.id);
                    });
                }

                nameFilter.label = 'Discover.recipientEditor.filters.savedSearch.name';

                // exchange listType filter for searchType filter
                this.filters = this.filters.filter(x => x.id !== 'listType');
                if (!this.filters.find(x => x.id === 'searchType')) {
                    this.filters.splice(1, 0, this.searchTypeFilter);
                }

                this.setFilters(this.filters);
                break;

            case DistributionSourceType.lists:
                this.recipientRows = this.listRecipientRows;

                nameFilter.label = 'Discover.recipientEditor.filters.list.name';

                // exchange searchType filter for listType filter
                this.filters = this.filters.filter(x => x.id !== 'searchType');
                if (!this.filters.find(x => x.id === 'listType')) {
                    this.filters.splice(1, 0, this.listTypeFilter);
                }

                this.setFilters(this.filters);
                break;

            // currently not being used, but might add it down the road
            // case DistributionSourceType.portfolios:
            //     this.recipientRows = this.portfolioRecipientRows;
            //     break;

            // case DistributionSourceType.mailingLists:
            //     this.recipientRows = this.mailingListRecipientRows;
            //     break;
        }

        if (clearFilters) {
            this.clearFilters();
            this.applyPreferenceFilters(false);
        }

        this.getFilterOptions(this.recipientRows);
        this.filteredRecipientRows$.next(this.recipientRows);
        this.applyPreferenceFilters();
    }

    getRecipientRows(): Observable<RecipientRow[]> {
        return this.filteredRecipientRows$;
    }

    getCurrentRecipientInformation(): Observable<RecipientInformationModel> {
        return this.currentRecipientInformation$;
    }

    getExcludedRecipients(): Observable<ExcludedRecipientApiModel[]> {
        return this.excludedRecipients$;
    }

    updateRecommendedRecipient(row: RecipientRow) {
        this.currentRecipientInformation.recommendedRecipientsListId = row.id;
        row.name = this.localizationService.transform('Discover.recipientEditor.labels.prCoPilotRecipients');

        // we need to update the count for excluded recipients
        for (const excludeRecipients of this.excludedRecipients) {
            if (row.count && (excludeRecipients.listId === row.id)) {
                row.count--;
            }
        }

        this.currentRecipientInformation.addList(row);
        this.currentRecipientInformation$.next(this.currentRecipientInformation);
        this.updateRecipients();
    }

    addRecipient(distributionSourceType: DistributionSourceType, row: RecipientRow) {
        if (distributionSourceType === DistributionSourceType.searches) {
            this.currentRecipientInformation.addSearch(row);
        } else if (distributionSourceType === DistributionSourceType.lists) {
            this.currentRecipientInformation.addList(row);
        } else if (distributionSourceType === DistributionSourceType.portfolios) {
            this.currentRecipientInformation.addPortfolioRow(row);
        } else {
            this.currentRecipientInformation.addMailingList(row);
        }
        this.currentRecipientInformation$.next(this.currentRecipientInformation);
        this.updateRecipients();
    }

    removeRecipientByCheckbox(distributionSourceType: DistributionSourceType, row: RecipientRow) {
        if (distributionSourceType === DistributionSourceType.searches) {
            this.currentRecipientInformation.removeSearch(row);
        } else if (distributionSourceType === DistributionSourceType.lists) {
            this.currentRecipientInformation.removeList(row);
        } else if (distributionSourceType === DistributionSourceType.portfolios) {
            this.currentRecipientInformation.removePortfolio(row);
        } else {
            this.currentRecipientInformation.removeMailingList(row);
        }
        this.currentRecipientInformation$.next(this.currentRecipientInformation);
        this.updateRecipients();
    }

    removeRecipientByTrashIcon(recipient: DistributionRecipientModel) {
        if (recipient.targetingSearchId) {
            this.currentRecipientInformation.removeSearch(recipient);
            const tuple = this.searchRecipientRows.find(x => x.id === recipient.targetingSearchId);
            if (tuple) {
                tuple.editDisabled = false;
            }
        } else if (recipient.targetingListId) {
            this.currentRecipientInformation.removeList(recipient);
        } else if (recipient.portfolioId) {
            this.currentRecipientInformation.removePortfolio(recipient);
        }  else {
            this.currentRecipientInformation.removeMailingList(recipient);
        }
        this.setSelections(); // Update checkboxs in the grid
        this.currentRecipientInformation$.next(this.currentRecipientInformation);
        this.updateRecipients();
    }

    toggleSearches(searchIds: number[], action: ToggleAction, distributionId: number = this.distributionId): Observable<void> {
        return this.distributionApi.toggleRecipientSearch(distributionId, action, searchIds).pipe(
            switchMap(() => this.distributionService.refetchDistribution(distributionId)),
            tap(() => {
                this.currentRecipientInformation.toggleLockState(searchIds, action);
                this.currentRecipientInformation$.next(this.currentRecipientInformation);

                this.filteredRecipientRows.forEach(row => {
                    if (searchIds.includes(row.id)) {
                        row.editDisabled = action === ToggleAction.LOCK;
                    }
                });
                this.filteredRecipientRows$.next(this.filteredRecipientRows);
                this.updateRecipients();
            }),
            catchError(() => {
                const actionLabel = action === ToggleAction.LOCK ? 'Lock' : 'Unlock';
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Discover.distribution.toggleLockSearch.error.heading', { action: actionLabel})
                });

                return EMPTY;
            }),
        );
    }

    setEmailRecipientCount() {
        if (this.currentRecipientInformation) {
            this.currentRecipientInformation.getOrCreateGroup(RecipientType.email).total =
                this.currentRecipientInformation.getOrCreateGroup(RecipientType.email).emailRecipients.length;
            this.currentRecipientInformation.calculateTotals();
            this.currentRecipientInformation$.next(this.currentRecipientInformation);
            this.updateRecipients();
        }
    }


    removeExclusion(recipient: DistributionRecipientModel, entity: TargetingResultModel) {
        const listId = !!recipient.targetingListId ? recipient.targetingListId : recipient.portfolioId;
        const index = this.excludedRecipients.findIndex(excludedRecipient => {
            return excludedRecipient.listId === listId &&
                excludedRecipient.searchId === recipient.targetingSearchId &&
                excludedRecipient.outletId === (entity.entityType === EntityType.OUTLET ? entity.outletModel.id : null) &&
                excludedRecipient.contactId === (entity.entityType === EntityType.CONTACT ? entity.contactModel.id : null);
        });
        this.excludedRecipients.splice(index, 1);
        this.excludedRecipients$.next(this.excludedRecipients);
        this.updateRecipientGroupCount(recipient, false, entity);
        this.updateRecipients();

        const name = (entity.entityType === EntityType.CONTACT) ? entity.contactName : entity.name;

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Discover.targeting.actions.includeRecipient.successHeading'),
            detail: this.localizationService.transform('Discover.targeting.actions.includeRecipient.successMessage', { name: name })
        });
    }

    addExclusion(recipient: DistributionRecipientModel, entity: TargetingResultModel) {
        const listId = !!recipient.targetingListId ? recipient.targetingListId : recipient.portfolioId;

        const excludedRecipient: ExcludedRecipientApiModel = {
            listId : listId,
            searchId : recipient.targetingSearchId,
            outletId : entity.entityType === EntityType.OUTLET ? entity.outletModel.id : null,
            contactId : entity.entityType === EntityType.CONTACT ? entity.contactModel.id : null,
        };

        this.excludedRecipients.push(excludedRecipient);
        this.excludedRecipients$.next(this.excludedRecipients);
        this.updateRecipientGroupCount(recipient, true, entity);
        this.updateRecipients();

        let name: string;
        if (entity.entityType === EntityType.CONTACT) {
            name = entity.contactName;
        } else if (entity.entityType === EntityType.OUTLET) {
            name = entity.name;
        }
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Discover.targeting.actions.excludeRecipient.successHeading'),
            detail: this.localizationService.transform('Discover.targeting.actions.excludeRecipient.successMessage', { name: name })
        });
    }

    private updateRecipientGroupCount(recipient: DistributionRecipientModel, addExclusion: boolean, entity: TargetingResultModel): void {
        this.currentRecipientInformation.recipientGroups.forEach(recipientGroup => {
            recipientGroup.recipients.forEach(recipientItem => {
                if ((recipient.targetingListId && recipientItem.targetingListId === recipient.targetingListId)
                    || (recipient.targetingSearchId && recipientItem.targetingSearchId === recipient.targetingSearchId)) {
                    if (addExclusion) {
                        recipientItem.count--;
                        recipientGroup.total--;
                        if (recipientGroup.type === RecipientType.individual) {
                            if (entity?.entityType === EntityType.CONTACT) {
                                recipientItem.contactCount--;
                            }
                            if (entity?.entityType === EntityType.OUTLET) {
                                recipientItem.outletCount--;
                            }
                        }
                    } else {
                        recipientItem.count++;
                        recipientGroup.total++;
                        if (recipientGroup.type === RecipientType.individual) {
                            if (entity?.entityType === EntityType.CONTACT) {
                                recipientItem.contactCount++;
                            }
                            if (entity?.entityType === EntityType.OUTLET) {
                                recipientItem.outletCount++;
                            }
                        }
                    }
                }
            });
        });

        this.currentRecipientInformation.calculateTotals();
        this.currentRecipientInformation$.next(this.currentRecipientInformation);
    }

    /**
     * Calculate available filter options (facets numbers) based on result items
     */
    private getFilterOptions(rows: RecipientRow[]) {
        this.getAuditableFilterOptions(rows, true);

        const listTypeBothOpt = this.listTypeFilter.options.find(x => x.value === TargetedListTypesEnum.both);
        const listTypeContactOpt = this.listTypeFilter.options.find(x => x.value === TargetedListTypesEnum.contact);
        const listTypeOutletOpt = this.listTypeFilter.options.find(x => x.value === TargetedListTypesEnum.outlet);
        const listTypeEmptyOpt = this.listTypeFilter.options.find(x => x.value === TargetedListTypesEnum.empty);

        const searchTypeContactOpt = this.searchTypeFilter.options.find(x => x.value === SearchModeEnum.contact);
        const searchTypeOutletOpt = this.searchTypeFilter.options.find(x => x.value === SearchModeEnum.outlet);

        // clear existing counts
        this.listTypeFilter.options.forEach(x => x.count = 0);
        this.searchTypeFilter.options.forEach(x => x.count = 0);

        rows.forEach(row => {
            if (row.hasContacts && row.hasOutlets) {
                listTypeBothOpt.count++;
            } else if (row.hasContacts) {
                listTypeContactOpt.count++;
            } else if (row.hasOutlets) {
                listTypeOutletOpt.count++;
            } else if (row.count === 0) {
                listTypeEmptyOpt.count++;
            }

            if (row.searchType === SearchModeEnum.contact) {
                searchTypeContactOpt.count++;
            } else if (row.searchType === SearchModeEnum.outlet) {
                searchTypeOutletOpt.count++;
            }
        });
    }

    applyPreferenceFilters(applyFilters: boolean = true) {
        let userPreferenceFilters: UserAppPreferenceSavedSearchesFilters | UserAppPreferenceTargetedListsFilters;
        if (this.sourceType === DistributionSourceType.lists) {
            userPreferenceFilters = this.listUserPrefsFilters;
        }

        if (this.sourceType === DistributionSourceType.searches) {
            userPreferenceFilters = this.searchUserPrefsFilters;
        }
        if (userPreferenceFilters) {
            const filterOptions = [
                { filterId: 'createdBy', prefProp: 'createdBy' },
                { filterId: 'modifiedBy', prefProp: 'modifiedBy' },
                { filterId: 'createdDate', prefProp: 'createdDate' },
                { filterId: 'modifiedDate', prefProp: 'modifiedDate' },
                { filterId: 'listType', prefProp: 'type' },
                { filterId: 'name', prefProp: 'name' },
                { filterId: 'visibility', prefProp: 'visibility' },
                { filterId: 'searchType', prefProp: 'type' }
            ];
            this.filters.forEach(_filter => {
                const filterProp = filterOptions.find(option => option.filterId === _filter.id)?.prefProp;
                const value = userPreferenceFilters?.[filterProp];
                if (value !== undefined && value !== null) {
                    // For the 'searchType' filter, the expected value is a string (e.g., "CONTACT"),
                    // but the saved user preference stores it as an array (e.g., ["CONTACT"]).
                    if (_filter.id === 'searchType' ) {
                        if (Array.isArray(value) && value.length > 0) {
                            _filter.setCurrentValue(value[0]);
                        } else {
                            _filter.setCurrentValue(null);
                        }
                    } else {
                        _filter.setCurrentValue(value);
                    }

                }
            });
            if (applyFilters) {
                this.getFilterOptions(this.recipientRows);
            }
        }
    }

    clearFilters() {
        this.createdByFilter.setCurrentValue([]);
        this.createdByFilter.options = [];
        this.modifiedByFilter.setCurrentValue([]);
        this.modifiedByFilter.options = [];
        super.clearFilters();
    }

    private updateUserLabels(rows: RecipientRow[], userMap: Map<number, UserModel>) {
        rows.forEach(row => {
            const createdUser = userMap.get(row.createdByUserId);
            row.createdUser = (createdUser) ? createdUser.getDisplayName() : this.localizationService.transform('Common.not.available');

            const modifiedUser = userMap.get(row.modifiedByUserId);
            row.modifiedUser = (modifiedUser) ? modifiedUser.getDisplayName() : this.localizationService.transform('Common.not.available');
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
